import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getUpdatedProducts } from "../../redux/thunk";
import type {
  DeliveryRecord,
  InventoryProduct,
  PurchaseHistory,
  UpdatedProduct,
  UsageHistory,
} from "../../types/inventory";
import { IoChevronBack } from "react-icons/io5";
import PurchaseEntryModal from "../../components/modals/PurchaseEntryModal";
import Pagination from "../../components/Pagination";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import { getInventoryOverview } from "../../redux/thunk";

const ACTIVITY_ITEMS_PER_PAGE = 10;

const STORAGE_PREFIX = "inv-product-";

function parseProductFromStorage(productId: string): InventoryProduct | null {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${productId}`);
    if (!raw) return null;
    return JSON.parse(raw) as InventoryProduct;
  } catch {
    return null;
  }
}

function formatShortNumericDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
}

function formatTimeOnly(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function getStockStatusLabel(status: string): string {
  switch (status) {
    case "Red":
      return "Out of Stock";
    case "Yellow":
      return "Low Level";
    case "Green":
      return "Healthy Level";
    default:
      return status;
  }
}

function getStockStatusPillClass(status: string): string {
  switch (status) {
    case "Red":
      return "text-red-700 bg-red-50";
    case "Yellow":
      return "text-amber-700 bg-amber-50";
    case "Green":
      return "text-green-700 bg-green-50";
    default:
      return "text-gray-700 bg-gray-50";
  }
}

/** Align with overview table mapping — API may use snake_case or alternate keys */
function getProductCategoryState(
  product: InventoryProduct & Record<string, unknown>
) {
  const str = (k: string) =>
    typeof product[k] === "string" ? (product[k] as string).trim() : "";
  const category =
    str("category") ||
    str("product_category") ||
    product.category?.trim() ||
    "";
  const state =
    str("state") ||
    str("product_state") ||
    str("warehouse_state") ||
    product.state?.trim() ||
    str("location") ||
    str("warehouse") ||
    str("store_location") ||
    "";
  return {
    category: category || "—",
    state: state || "—",
  };
}

/** Same keys as overview table (`SalesInventory` overviewProductExtras.size) */
function getProductSizeLabel(
  product: InventoryProduct & Record<string, unknown>
): string {
  const str = (k: string) =>
    typeof product[k] === "string" ? (product[k] as string).trim() : "";
  const size =
    str("size") ||
    str("package_size") ||
    str("packageSize") ||
    str("weight") ||
    "";
  return size || "—";
}

/** Supports camelCase or snake_case from API */
function getUsageRowFields(row: UsageHistory & Record<string, unknown>) {
  const orderDateRaw =
    (row.orderDate as string | undefined) ??
    (row.order_date as string | undefined) ??
    row.usedAt;
  const deliveryDateRaw =
    (row.deliveryDate as string | null | undefined) ??
    (row.delivery_date as string | null | undefined);
  const customer =
    (row.customer as string | undefined)?.trim() ||
    (row.customer_name as string | undefined)?.trim() ||
    "";
  const rider =
    (row.rider as string | null | undefined)?.trim() ||
    (row.rider_name as string | undefined)?.trim() ||
    (row.delivered_by as string | undefined)?.trim() ||
    "";
  const status =
    (row.status as string | undefined)?.trim() ||
    (row.order_status as string | undefined)?.trim() ||
    "";
  const quantityUsed = Number(row.quantityUsed);
  const quantityLabel =
    Number.isFinite(quantityUsed) && quantityUsed !== 0
      ? quantityUsed < 0
        ? String(quantityUsed)
        : `-${Math.abs(quantityUsed)}`
      : "—";

  return {
    orderDateRaw,
    deliveryDateRaw,
    customer,
    rider,
    status,
    quantityLabel,
  };
}

function getDeliveryRowFields(
  row: DeliveryRecord & Record<string, unknown>,
  fallbackProductName: string
) {
  const buyer =
    (row.customer as string | null | undefined)?.trim() ||
    (row.customer_name as string | undefined)?.trim() ||
    (row.buyer as string | undefined)?.trim() ||
    (row.buyer_name as string | undefined)?.trim() ||
    (row.orderedBy as string | undefined)?.trim() ||
    (row.ordered_by as string | undefined)?.trim() ||
    "";
  const recipient =
    (row.deliveredTo as string | null | undefined)?.trim() ||
    (row.recipient as string | undefined)?.trim() ||
    (row.delivered_to as string | undefined)?.trim() ||
    "";
  const productLine =
    (row.productName as string | null | undefined)?.trim() ||
    (row.product_name as string | undefined)?.trim() ||
    fallbackProductName;
  const rawQty =
    row.quantity !== undefined && row.quantity !== null
      ? row.quantity
      : (row.quantity_delivered as number | null | undefined);
  const quantity =
    rawQty !== undefined && rawQty !== null && !Number.isNaN(Number(rawQty))
      ? Number(rawQty)
      : null;
  const orderRef =
    (row.orderId as string | null | undefined)?.trim() ||
    (row.order_id as string | undefined)?.trim() ||
    "";
  const riderName =
    (row.rider as string | null | undefined)?.trim() ||
    (row.rider_name as string | undefined)?.trim() ||
    (row.deliveredBy as string | null | undefined)?.trim() ||
    (row.delivered_by as string | undefined)?.trim() ||
    "";

  return { buyer, recipient, productLine, quantity, orderRef, riderName };
}

function purchaseHistoryHasOrderId(
  row: PurchaseHistory & Record<string, unknown>
): boolean {
  const id = row.orderId ?? row.order_id;
  return typeof id === "string" && id.trim().length > 0;
}

function purchaseHistoryRowQuantity(
  row: PurchaseHistory & Record<string, unknown>
): number {
  const q = Number(row.quantityPurchased ?? row.quantity_purchased ?? 0);
  return Number.isFinite(q) ? q : 0;
}

function applyUpdatedProduct(
  updated: UpdatedProduct,
  previous: InventoryProduct
): InventoryProduct {
  return {
    ...previous,
    current_stock: updated.quantity,
    initial_stock: updated.initialQuantity ?? previous.initial_stock,
    updatedAt: updated.updatedAt,
    last_purchase_date: updated.updatedAt,
    stock_status:
      updated.quantity <= 0
        ? "Red"
        : updated.quantity <= 10
          ? "Yellow"
          : "Green",
  };
}

const InventoryProductDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const inventoryOverview = useAppSelector(
    (state) => state.inventory.inventoryOverview
  );

  const stateProduct = (location.state as { product?: InventoryProduct } | null)
    ?.product;

  const product = useMemo(() => {
    if (!productId) return null;
    if (stateProduct && stateProduct.product_id === productId) {
      return stateProduct;
    }
    const fromRedux = inventoryOverview.data.find(
      (p) => p.product_id === productId
    );
    if (fromRedux) return fromRedux;
    return parseProductFromStorage(productId);
  }, [productId, stateProduct, inventoryOverview.data]);

  const [initialLoading, setInitialLoading] = useState(!product);

  useEffect(() => {
    if (product) {
      setInitialLoading(false);
      return;
    }
    if (inventoryOverview.status === "loading") return;
    if (inventoryOverview.data.length === 0 && inventoryOverview.status === "idle") {
      dispatch(getInventoryOverview({ page: 1, limit: 500 }));
      return;
    }
    setInitialLoading(false);
  }, [product, inventoryOverview.status, inventoryOverview.data.length, dispatch]);

  useEffect(() => {
    if (!product?.product_id) return;
    try {
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${product.product_id}`,
        JSON.stringify(product)
      );
    } catch {
      /* ignore quota */
    }
  }, [product]);

  const totalPurchased = useMemo(() => {
    if (!product?.purchase_history?.length) return 0;
    return product.purchase_history
      .filter(
        (row) =>
          !purchaseHistoryHasOrderId(row as PurchaseHistory & Record<string, unknown>)
      )
      .reduce(
        (sum, row) =>
          sum +
          purchaseHistoryRowQuantity(row as PurchaseHistory & Record<string, unknown>),
        0
      );
  }, [product]);

  const totalUsed = useMemo(() => {
    let sum = 0;
    if (product?.usage_history?.length) {
      sum += product.usage_history.reduce((s, row) => {
        const q = Number(row.quantityUsed);
        if (!Number.isFinite(q)) return s;
        return s + Math.abs(q);
      }, 0);
    }
    if (product?.purchase_history?.length) {
      sum += product.purchase_history
        .filter((row) =>
          purchaseHistoryHasOrderId(row as PurchaseHistory & Record<string, unknown>)
        )
        .reduce(
          (s, row) =>
            s +
            purchaseHistoryRowQuantity(
              row as PurchaseHistory & Record<string, unknown>
            ),
          0
        );
    }
    return sum;
  }, [product]);

  const procurementPurchases = useMemo(() => {
    if (!product?.purchase_history?.length) return [];
    return [...product.purchase_history]
      .filter(
        (row) =>
          !purchaseHistoryHasOrderId(row as PurchaseHistory & Record<string, unknown>)
      )
      .sort(
        (a, b) =>
          new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime()
      );
  }, [product]);

  const combinedDepletionRows = useMemo(() => {
    type DepletionRow =
      | { kind: "usage"; row: UsageHistory; sortMs: number }
      | {
          kind: "order_sale";
          row: PurchaseHistory & Record<string, unknown>;
          sortMs: number;
        };
    const out: DepletionRow[] = [];
    if (product?.usage_history?.length) {
      for (const row of product.usage_history) {
        const ext = row as UsageHistory & Record<string, unknown>;
        const d =
          (ext.orderDate as string | undefined) ??
          (ext.order_date as string | undefined) ??
          row.usedAt;
        out.push({
          kind: "usage",
          row,
          sortMs: new Date(d).getTime(),
        });
      }
    }
    if (product?.purchase_history?.length) {
      for (const row of product.purchase_history) {
        const ext = row as PurchaseHistory & Record<string, unknown>;
        if (!purchaseHistoryHasOrderId(ext)) continue;
        out.push({
          kind: "order_sale",
          row: ext,
          sortMs: new Date(row.purchasedAt).getTime(),
        });
      }
    }
    out.sort((a, b) => b.sortMs - a.sortMs);
    return out;
  }, [product]);

  const sortedDeliveries = useMemo(() => {
    if (!product?.delivery_records) return [];
    return [...product.delivery_records].sort(
      (a, b) =>
        new Date(b.deliveryTime).getTime() -
        new Date(a.deliveryTime).getTime()
    );
  }, [product]);

  const [activityTab, setActivityTab] = useState<
    "purchase" | "usage" | "delivery"
  >("purchase");
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [procurementPage, setProcurementPage] = useState(1);
  const [depletionPage, setDepletionPage] = useState(1);
  const [deliveryPage, setDeliveryPage] = useState(1);

  const paginatedProcurement = useMemo(() => {
    const start = (procurementPage - 1) * ACTIVITY_ITEMS_PER_PAGE;
    return procurementPurchases.slice(start, start + ACTIVITY_ITEMS_PER_PAGE);
  }, [procurementPurchases, procurementPage]);

  const paginatedDepletion = useMemo(() => {
    const start = (depletionPage - 1) * ACTIVITY_ITEMS_PER_PAGE;
    return combinedDepletionRows.slice(start, start + ACTIVITY_ITEMS_PER_PAGE);
  }, [combinedDepletionRows, depletionPage]);

  const paginatedDeliveries = useMemo(() => {
    const start = (deliveryPage - 1) * ACTIVITY_ITEMS_PER_PAGE;
    return sortedDeliveries.slice(start, start + ACTIVITY_ITEMS_PER_PAGE);
  }, [sortedDeliveries, deliveryPage]);

  useEffect(() => {
    if (!product?.product_id) return;
    setProcurementPage(1);
    setDepletionPage(1);
    setDeliveryPage(1);
  }, [product?.product_id]);

  useEffect(() => {
    const max = Math.max(
      1,
      Math.ceil(procurementPurchases.length / ACTIVITY_ITEMS_PER_PAGE)
    );
    if (procurementPage > max) setProcurementPage(max);
  }, [procurementPurchases.length, procurementPage]);

  useEffect(() => {
    const max = Math.max(
      1,
      Math.ceil(combinedDepletionRows.length / ACTIVITY_ITEMS_PER_PAGE)
    );
    if (depletionPage > max) setDepletionPage(max);
  }, [combinedDepletionRows.length, depletionPage]);

  useEffect(() => {
    const max = Math.max(
      1,
      Math.ceil(sortedDeliveries.length / ACTIVITY_ITEMS_PER_PAGE)
    );
    if (deliveryPage > max) setDeliveryPage(max);
  }, [sortedDeliveries.length, deliveryPage]);

  if (!productId) {
    return null;
  }

  if (initialLoading || (!product && inventoryOverview.status === "loading")) {
    return (
      <main className="flex items-center justify-center w-full min-h-[60vh]">
        <LoadingSpinner />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="w-full max-w-5xl mx-auto p-6">
        <p className="text-gray-600 mb-4">Product not found.</p>
        <button
          type="button"
          onClick={() => navigate("/dashboard/sales-inventory")}
          className="text-green-700 font-medium flex items-center gap-1 hover:underline"
        >
          <IoChevronBack className="w-5 h-5" />
          Back to inventory
        </button>
      </main>
    );
  }

  const { category: categoryLabel, state: stateLabel } = getProductCategoryState(
    product as InventoryProduct & Record<string, unknown>
  );
  const sizeLabel = getProductSizeLabel(
    product as InventoryProduct & Record<string, unknown>
  );
  const lastPurchaseDisplay = formatShortNumericDate(
    product.last_purchase_date
  );

  return (
    <main className="w-full overflow-x-auto pb-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="pt-2 pb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Inventory Overview
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track and manage all products in real-time
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate("/dashboard/sales-inventory")}
            className="flex items-center gap-1 text-gray-700 hover:text-gray-900 text-sm font-medium w-fit"
          >
            <IoChevronBack className="w-5 h-5" />
            Back to Dashboard
          </button>
          <button
            type="button"
            onClick={() => setPurchaseModalOpen(true)}
            className="inline-flex items-center justify-center rounded-md bg-green-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-800 transition-colors"
          >
            + Add Purchase
          </button>
        </div>

        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-6">
          <div className="flex flex-wrap items-start gap-3 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex-1 min-w-[200px]">
              {product.product_name}
            </h2>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getStockStatusPillClass(
                product.stock_status
              )}`}
            >
              {getStockStatusLabel(product.stock_status)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-6 flex flex-wrap items-center gap-x-0 gap-y-1">
            <span>
              <span className="font-medium text-gray-700">Category:</span>{" "}
              {categoryLabel}
            </span>
            <span className="mx-3 text-gray-300" aria-hidden>
              |
            </span>
            <span>
              <span className="font-medium text-gray-700">Size:</span>{" "}
              {sizeLabel}
            </span>
            <span className="mx-3 text-gray-300" aria-hidden>
              |
            </span>
            <span>
              <span className="font-medium text-gray-700">State:</span>{" "}
              {stateLabel}
            </span>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Current Stock
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {product.current_stock} Units
              </p>
              <p className="text-xs text-gray-400 mt-1 leading-snug">
                Total on hand. Add Purchase adds units to this balance; the
                figure is not &quot;only&quot; the amount you last entered.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total procured (all time)
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {totalPurchased} Units
              </p>
              <p className="text-xs text-gray-400 mt-1">
                From procurement / stock top-ups recorded for this product
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total depleted (orders)
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {totalUsed} Units
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Sum of quantities in usage / order history (stock out to
                customers)
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Last procurement (most recent)
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {lastPurchaseDisplay}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 pb-0">
            <h3 className="text-lg font-semibold text-gray-900">
              Product Activity History
            </h3>
            <div className="flex flex-wrap gap-6 mt-4 border-b border-gray-200">
              {(
                [
                  ["purchase", "Procurement & top-ups"],
                  ["usage", "Orders & depletion"],
                  ["delivery", "Delivery record"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActivityTab(key)}
                  className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activityTab === key
                      ? "text-green-700 border-green-700"
                      : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6 pt-4">
            {activityTab === "purchase" && (
              <div className="overflow-x-auto">
                <p className="text-sm text-gray-600 mb-3">
                  Admin procurement and stock top-ups only. Customer order lines
                  (with an order ID) appear under{" "}
                  <span className="font-medium text-gray-800">
                    Orders &amp; depletion
                  </span>
                  .
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-500">
                      <th className="p-3 font-medium rounded-tl-lg">Purchase Date</th>
                      <th className="p-3 font-medium">Time</th>
                      <th className="p-3 font-medium">Quantity</th>
                      <th className="p-3 font-medium rounded-tr-lg">
                        Procurement Officer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {procurementPurchases.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="p-8 text-center text-gray-500 italic"
                        >
                          No procurement or top-up history yet
                        </td>
                      </tr>
                    ) : (
                      paginatedProcurement.map((row, idx) => (
                        <tr
                          key={`${row.purchasedAt}-${
                            (procurementPage - 1) * ACTIVITY_ITEMS_PER_PAGE + idx
                          }`}
                          className="border-t border-gray-100"
                        >
                          <td className="p-3 text-gray-800">
                            {formatShortNumericDate(row.purchasedAt)}
                          </td>
                          <td className="p-3 text-gray-600">
                            {formatTimeOnly(row.purchasedAt)}
                          </td>
                          <td className="p-3 font-medium text-green-700">
                            +
                            {purchaseHistoryRowQuantity(
                              row as PurchaseHistory & Record<string, unknown>
                            )}
                          </td>
                          <td className="p-3 text-gray-800">
                            {row.customer?.trim() || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {procurementPurchases.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Pagination
                      itemsPerPage={ACTIVITY_ITEMS_PER_PAGE}
                      length={procurementPurchases.length}
                      currentPage={procurementPage}
                      handlePagination={setProcurementPage}
                      prevPage={() =>
                        setProcurementPage((p) => Math.max(1, p - 1))
                      }
                      nextPage={() =>
                        setProcurementPage((p) =>
                          Math.min(
                            Math.max(
                              1,
                              Math.ceil(
                                procurementPurchases.length /
                                  ACTIVITY_ITEMS_PER_PAGE
                              )
                            ),
                            p + 1
                          )
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {activityTab === "usage" && (
              <div className="overflow-x-auto">
                <p className="text-sm text-gray-600 mb-3">
                  Customer orders and stock depletion. Includes{" "}
                  <span className="font-medium text-gray-800">usage history</span>{" "}
                  and purchase lines tied to an{" "}
                  <span className="font-medium text-gray-800">order ID</span>.
                </p>
                <table className="w-full text-sm min-w-[900px]">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="p-3 font-medium rounded-tl-lg">
                        Order ID
                      </th>
                      <th className="p-3 font-medium">Customer</th>
                      <th className="p-3 font-medium text-center">Quantity</th>
                      <th className="p-3 font-medium">Order Date</th>
                      <th className="p-3 font-medium">Delivery Date</th>
                      <th className="p-3 font-medium">Rider</th>
                      <th className="p-3 font-medium rounded-tr-lg">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedDepletionRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="p-8 text-center text-gray-500 italic"
                        >
                          No order or depletion history
                        </td>
                      </tr>
                    ) : (
                      paginatedDepletion.map((item, idx) => {
                        const rowIndex =
                          (depletionPage - 1) * ACTIVITY_ITEMS_PER_PAGE + idx;
                        if (item.kind === "usage") {
                          const row = item.row;
                          const ext = row as UsageHistory &
                            Record<string, unknown>;
                          const f = getUsageRowFields(ext);
                          return (
                            <tr
                              key={`usage-${row.orderId}-${row.usedAt}-${rowIndex}`}
                              className="border-t border-gray-100"
                            >
                              <td className="p-3 text-gray-900 font-mono text-xs">
                                {row.orderId}
                              </td>
                              <td className="p-3 text-gray-800">
                                {f.customer || "—"}
                              </td>
                              <td className="p-3 text-center">
                                <span className="inline-block min-w-[2.5rem] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                                  {f.quantityLabel}
                                </span>
                              </td>
                              <td className="p-3 text-gray-800 whitespace-nowrap">
                                {formatShortNumericDate(f.orderDateRaw)}
                              </td>
                              <td className="p-3 text-gray-800 whitespace-nowrap">
                                {f.deliveryDateRaw
                                  ? formatShortNumericDate(f.deliveryDateRaw)
                                  : "—"}
                              </td>
                              <td className="p-3 text-gray-800">
                                {f.rider || "—"}
                              </td>
                              <td className="p-3 text-gray-800">
                                {f.status || "—"}
                              </td>
                            </tr>
                          );
                        }
                        const row = item.row;
                        const orderId = String(
                          row.orderId ?? row.order_id ?? ""
                        );
                        const qty = Number(row.quantityPurchased);
                        return (
                          <tr
                            key={`sale-${orderId}-${row.purchasedAt}-${rowIndex}`}
                            className="border-t border-gray-100"
                          >
                            <td className="p-3 text-gray-900 font-mono text-xs">
                              {orderId || "—"}
                            </td>
                            <td className="p-3 text-gray-800">
                              {String(row.customer ?? "").trim() || "—"}
                            </td>
                            <td className="p-3 text-center">
                              <span className="inline-block min-w-[2.5rem] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                                {Number.isFinite(qty) && qty !== 0
                                  ? `-${Math.abs(qty)}`
                                  : "—"}
                              </span>
                            </td>
                            <td className="p-3 text-gray-800 whitespace-nowrap">
                              {formatShortNumericDate(row.purchasedAt)}
                            </td>
                            <td className="p-3 text-gray-600 whitespace-nowrap">
                              —
                            </td>
                            <td className="p-3 text-gray-600">—</td>
                            <td className="p-3 text-gray-600">—</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                {combinedDepletionRows.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Pagination
                      itemsPerPage={ACTIVITY_ITEMS_PER_PAGE}
                      length={combinedDepletionRows.length}
                      currentPage={depletionPage}
                      handlePagination={setDepletionPage}
                      prevPage={() =>
                        setDepletionPage((p) => Math.max(1, p - 1))
                      }
                      nextPage={() =>
                        setDepletionPage((p) =>
                          Math.min(
                            Math.max(
                              1,
                              Math.ceil(
                                combinedDepletionRows.length /
                                  ACTIVITY_ITEMS_PER_PAGE
                              )
                            ),
                            p + 1
                          )
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {activityTab === "delivery" && (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Deliveries shown here come from the{" "}
                  <span className="font-mono text-xs text-gray-700">
                    delivery_records
                  </span>{" "}
                  array on this product as returned by the API (same payload as
                  inventory / product detail).
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  All delivery records for orders containing{" "}
                  <span className="font-medium text-gray-800">
                    {product.product_name}
                  </span>{" "}
                  ({sortedDeliveries.length}{" "}
                  {sortedDeliveries.length === 1 ? "record" : "records"})
                </p>
                {sortedDeliveries.length === 0 ? (
                  <p className="text-gray-500 text-sm italic py-8 text-center border border-dashed border-gray-200 rounded-lg">
                    No delivery records
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {paginatedDeliveries.map((row, idx) => {
                      const ext = row as DeliveryRecord &
                        Record<string, unknown>;
                      const f = getDeliveryRowFields(
                        ext,
                        product.product_name
                      );
                      const rowIndex =
                        (deliveryPage - 1) * ACTIVITY_ITEMS_PER_PAGE + idx;
                      return (
                        <li
                          key={`${row.deliveryTime}-${rowIndex}`}
                          className="rounded-lg border border-gray-100 bg-gradient-to-br from-gray-50 to-green-50/40 px-4 py-4 shadow-sm"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green-600"
                              aria-hidden
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-800">
                                <span className="font-medium text-gray-600">
                                  Customer (buyer):{" "}
                                </span>
                                <span className="font-semibold text-gray-900">
                                  {f.buyer || "—"}
                                </span>
                              </p>
                              <p className="text-sm text-gray-800 mt-1.5">
                                <span className="font-medium text-gray-600">
                                  Delivered to:{" "}
                                </span>
                                <span className="font-semibold text-green-700">
                                  {f.recipient || "—"}
                                </span>
                              </p>
                              <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                                {f.productLine}
                                {f.quantity != null &&
                                !Number.isNaN(f.quantity) ? (
                                  <span> ({f.quantity})</span>
                                ) : null}
                                {f.orderRef ? (
                                  <>
                                    {" "}
                                    <span className="text-gray-400">.</span>{" "}
                                    <span className="font-mono text-xs text-gray-800">
                                      {f.orderRef}
                                    </span>
                                  </>
                                ) : null}
                              </p>
                              <p className="text-sm mt-2">
                                <span className="font-medium text-gray-600">
                                  Rider:{" "}
                                </span>
                                <span className="text-gray-900">
                                  {f.riderName || "—"}
                                </span>
                              </p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {sortedDeliveries.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Pagination
                      itemsPerPage={ACTIVITY_ITEMS_PER_PAGE}
                      length={sortedDeliveries.length}
                      currentPage={deliveryPage}
                      handlePagination={setDeliveryPage}
                      prevPage={() =>
                        setDeliveryPage((p) => Math.max(1, p - 1))
                      }
                      nextPage={() =>
                        setDeliveryPage((p) =>
                          Math.min(
                            Math.max(
                              1,
                              Math.ceil(
                                sortedDeliveries.length /
                                  ACTIVITY_ITEMS_PER_PAGE
                              )
                            ),
                            p + 1
                          )
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <PurchaseEntryModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        product={product}
        onSuccess={async (updated: InventoryProduct) => {
          try {
            const res = await dispatch(
              getUpdatedProducts({ filter: "today" })
            ).unwrap();
            const products = (res as { products?: UpdatedProduct[] })
              ?.products;
            const fresh = products?.find(
              (p) => p._id === updated.product_id
            );
            const productToShow = fresh
              ? applyUpdatedProduct(fresh, updated)
              : updated;
            navigate(location.pathname, {
              state: { product: productToShow },
              replace: true,
            });
          } catch {
            navigate(location.pathname, {
              state: { product: updated },
              replace: true,
            });
          }
        }}
      />
    </main>
  );
};

export default InventoryProductDetail;
